import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getStandardCosts,
  getActualCosts,
  type ProductionCostRecord,
} from '@/services/costingService';
import CalculateCostModal from './components/CalculateCostModal';
import CostDetailSlideOver from './components/CostDetailSlideOver';
import CostSheetPrintModal from './components/CostSheetPrintModal';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { useWarehouseStore } from '@/stores/warehouseStore';

export default function OrderCostingPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [loading, setLoading] = useState(true);
  const [costRecords, setCostRecords] = useState<ProductionCostRecord[]>([]);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<'ALL' | 'STANDARD' | 'ACTUAL'>('ALL');
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [selectedCost, setSelectedCost] = useState<ProductionCostRecord | null>(null);
  const [printCost, setPrintCost] = useState<ProductionCostRecord | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [stdRes, actRes] = await Promise.all([
        getStandardCosts('', '2024-2025', selectedWarehouseId),
        getActualCosts('', 'all', selectedWarehouseId),
      ]);

      const list: ProductionCostRecord[] = [];
      if (stdRes && stdRes.success && stdRes.data) {
        list.push(...stdRes.data);
      }
      if (actRes && actRes.success && actRes.data) {
        list.push(...actRes.data);
      }
      setCostRecords(list);
    } catch (err) {
      console.error("Failed to load order costs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  const filtered = useMemo(() => {
    return costRecords.filter((cost) => {
      if (search) {
        const q = search.toLowerCase();
        const match =
          cost.productionOrderNumber.toLowerCase().includes(q) ||
          cost.productName.toLowerCase().includes(q) ||
          (cost.variantName ?? '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (methodFilter !== 'ALL' && cost.costingMethod !== methodFilter) return false;
      return true;
    });
  }, [costRecords, search, methodFilter]);

  const totals = useMemo(() => {
    return {
      count: filtered.length,
      material: filtered.reduce((s, c) => s + c.materialCost, 0),
      labour: filtered.reduce((s, c) => s + c.labourCost, 0),
      overhead: filtered.reduce((s, c) => s + c.overheadCost, 0),
      rejection: filtered.reduce((s, c) => s + c.rejectionCost, 0),
      total: filtered.reduce((s, c) => s + c.totalCost, 0),
    };
  }, [filtered]);

  const handleSaveCost = (newRecord: ProductionCostRecord) => {
    loadData();
    setShowCalcModal(false);
    showToast(`Cost calculated and saved for ${newRecord.productionOrderNumber}`);
  };

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Toast */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
            {toast}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/costing')}
              className="p-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer shadow-sm transition-colors"
              title="Back to Dashboard"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Production Order Costing</h1>
              <p className="text-sm text-gray-500 mt-0.5">Calculate and view manufacturing costs per production order</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/manufacturing/costing/setup')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-settings-4-line" />
              Settings
            </button>
            <button
              onClick={() => setShowCalcModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Calculate Cost
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">POs Costed</div>
            <div className="text-lg font-semibold text-gray-900">{totals.count}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Material</div>
            <div className="text-lg font-semibold text-orange-600">₹{totals.material.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Labour</div>
            <div className="text-lg font-semibold text-blue-600">₹{totals.labour.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Overhead</div>
            <div className="text-lg font-semibold text-violet-600">₹{totals.overhead.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Rejection</div>
            <div className="text-lg font-semibold text-red-600">₹{totals.rejection.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">Total</div>
            <div className="text-lg font-semibold text-gray-900">₹{totals.total.toLocaleString()}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search PO number, product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
          <div className="w-40">
            <SearchableSelect
              value={methodFilter}
              onChange={(val) => setMethodFilter(val as typeof methodFilter)}
              options={[
                { value: 'ALL', label: 'All Methods' },
                { value: 'STANDARD', label: 'Standard' },
                { value: 'ACTUAL', label: 'Actual' }
              ]}
              placeholder="All Methods"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">PO Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Product</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Qty</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Method</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Material</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Labour</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Overhead</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Total Cost</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Cost/Unit</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Sale Price</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Margin %</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((cost) => {
                  const marginColor =
                    cost.marginPct > 30 ? 'text-emerald-600' :
                    cost.marginPct >= 10 ? 'text-amber-600' : 'text-red-600';

                  return (
                    <tr key={cost.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{cost.productionOrderNumber}</td>
                      <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                        <div>{cost.productName}</div>
                        {cost.variantName && (
                          <div className="text-xs text-gray-500">{cost.variantName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        {cost.producedQty} {cost.unit}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            cost.costingMethod === 'STANDARD'
                              ? 'bg-sky-50 text-sky-700'
                              : 'bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          {cost.costingMethod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        ₹{cost.materialCost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        ₹{cost.labourCost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        ₹{cost.overheadCost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                        ₹{cost.totalCost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        ₹{cost.costPerUnit.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                        ₹{cost.salePrice.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span className={`text-xs font-semibold ${marginColor}`}>
                          {cost.marginPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                          {cost.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setSelectedCost(cost)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            title="View Cost Breakdown"
                          >
                            <i className="ri-eye-line text-base" />
                          </button>
                          <button
                            onClick={() => setPrintCost(cost)}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            title="Print Cost Sheet"
                          >
                            <i className="ri-printer-line text-base" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={13} className="px-4 py-8 text-center text-gray-500">
                      {loading ? 'Loading cost records...' : 'No production order cost records found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCalcModal && (
        <CalculateCostModal
          onClose={() => setShowCalcModal(false)}
          onSave={handleSaveCost}
        />
      )}

      {selectedCost && (
        <CostDetailSlideOver
          cost={selectedCost}
          onClose={() => setSelectedCost(null)}
        />
      )}

      {printCost && (
        <CostSheetPrintModal
          cost={printCost}
          onClose={() => setPrintCost(null)}
        />
      )}
    </AppLayout>
  );
}